import cn from 'classnames';
import { useMemo } from 'react';
import { Select } from '@/Framework/UI/Organisms/FinalForm/Fields/Select';
import { Field, useField } from '@/Framework/UI/Organisms/FinalForm';
import { usePortfolioMonitorContext } from '@/finsight/application/PortfolioMonitor/PortfolioMonitorContext';
import {
  PORTFOLIO_OPTIONAL_DATA_FIELD_ARRAY,
} from '@/finsight/ui/components/portfolioMonitor/PortfolioContent/PortfolioForm/FormSteps/OptionalData/constants';
import Input from '@dealroadshow/uikit/core/components/Input';
import Tooltip from '@dealroadshow/uikit/core/components/Tooltip';
import IconTrash from '@dealroadshow/uikit/core/components/Icon/IconTrash';
import { StrictModifiers } from '@popperjs/core';
import Button, { ButtonVariantType } from '@dealroadshow/uikit/core/components/Button';
import {
  IFinalFormFieldInputComponentProps,
} from '@/Framework/UI/Organisms/FinalForm/interfaces';
import {
  useConfirmDeletePositionContext,
} from '@/finsight/ui/components/portfolioMonitor/PortfolioContent/common/ConfirmDeletePositionContext/ConfirmDeletePositionContext';

import styles from
    '@/finsight/ui/components/portfolioMonitor/PortfolioContent/PortfolioForm/FormSteps/OptionalData/styles.scss';
import spaces from '@dealroadshow/uikit/core/styles/helpers/spaces.scss';

interface ICurrencyCellProps {
  rowIndex: number,
  cellName: string,
  isDisabled: boolean,
}

interface IInputCellProps {
  rowIndex: number,
  cellName: string,
  params: Partial<IFinalFormFieldInputComponentProps>,
  format: (inputValue: string) => void,
}

const popperModifiers: StrictModifiers[] = [
  {
    name: 'offset',
    options: {
      offset: [0, 1],
    },
  },
];

export const OptionalDataCurrencyCell = (props: ICurrencyCellProps) => {
  const {
    rowIndex,
    cellName,
    isDisabled,
  } = props;
  const { currencies: { collection } } = usePortfolioMonitorContext();

  const currenciesOptions = useMemo(() => collection.map((option) => ({
    value: option.id,
    label: option.displayName,
  })), [collection.length]);

  return (
    <Field
      name={ `${ PORTFOLIO_OPTIONAL_DATA_FIELD_ARRAY }[${ rowIndex }].${ cellName }` }
      placeholder="USD"
      formFieldClassName={ cn(spaces.mbn, styles.currencyCell) }
      className={ cn(styles.formInputWrp, styles.formInputWrpSmall) }
      component={ Select }
      dataTest={ cellName }
      simpleValue
      options={ currenciesOptions }
      isDisabled={ isDisabled }
    />
  );
};

export const OptionalDataInputCell = (props: IInputCellProps) => {
  const {
    cellName,
    rowIndex,
    params,
    format,
  } = props;
  const { meta } = useField(`${ PORTFOLIO_OPTIONAL_DATA_FIELD_ARRAY }[${ rowIndex }].${ cellName }`);

  return (
    <Tooltip
      content={ meta.error }
      containerClassName={ styles.errorTooltipContainer }
      className={ meta.error ? '' : styles.disabledTooltip }
      type={ Tooltip.CONSTANTS.ERROR }
      popperModifiers={ popperModifiers }
    >
      <Field
        name={ `${ PORTFOLIO_OPTIONAL_DATA_FIELD_ARRAY }[${ rowIndex }].${ cellName }` }
        placeholder="1,000,000"
        formFieldClassName={ cn(spaces.mbn, styles.inputField) }
        inputClassName={ { [styles.inputFieldError]: meta.error } }
        dataTest={ cellName }
        component={ Input }
        format={ format }
        isErrorMessage={ false }
        { ...params }
      />
    </Tooltip>
  );
};

export const ActionsCell = (props: { rowIndex: number }) => {
  const { rowIndex } = props;
  const { handleDeletePosition } = useConfirmDeletePositionContext();

  return (
    <Button
      dataTest="portfolioCusipListDeleteButton"
      onClick={ () => handleDeletePosition(rowIndex) }
      variant={ ButtonVariantType.link }
    >
      <IconTrash />
    </Button>
  );
};
